import { useEffect, useRef, useState } from 'react';

/**
 * Sankey tarzı huni grafiği — referans DXP admin panelindeki `FunnelPage` çizimiyle birebir.
 *
 * Yerleşim:
 *   sütun 0        → trafik kaynakları (opsiyonel, solda dikey yığılı)
 *   sütun 1        → giriş sayfası (mavi)
 *   sütun 2..N+1   → adım düğümleri (yeşil, omurga üzerinde dikey ortalı)
 *   sağ kenar      → çıkış düğümleri (kırmızı, omurganın altında yığılı)
 *
 * Her çıkış, adımına giren bağlantıdan aşağı doğru çatallanır; kurdele sağ kenardaki
 * yuvasına kadar uzanır.
 */

export interface FunnelChartSource {
  name: string;
  count: number;
}

export interface FunnelChartStep {
  label: string;
  visitors: number;
  convRate: number;
  exitCount: number;
  /** §94 (M49) — önceki 14 günlük pencerenin dönüşüm oranı; null/verilmemiş = kıyas yok. */
  prevConvRate?: number | null;
}

export interface FunnelModel {
  sources: FunnelChartSource[];
  entry: { label: string; count: number };
  steps: FunnelChartStep[];
  total: number;
  success: number;
}

interface HoverState {
  x: number;
  y: number;
  title: string;
  sub: string;
}

const tr = (n: number): string => n.toLocaleString('tr-TR');

/**
 * §94.1 — dönüşüm oranı tek ondalıkla ve tr-TR ayracıyla yazılır ('%42,1').
 * Sayı ARTIK tam sayı değildir (bkz. `Funnel.tsx#yuzde1`); `${p.convRate}` doğrudan
 * basılsaydı ondalık NOKTAYLA görünürdü — panelin tek biçimleme kuralına aykırı.
 */
const oran = (n: number | null | undefined): string =>
  typeof n === 'number' && Number.isFinite(n)
    ? n.toLocaleString('tr-TR', { maximumFractionDigits: 1 })
    : '—';

function FunnelChart({
  data,
  width,
  height,
}: {
  data: FunnelModel;
  width: number;
  height: number;
}) {
  const { sources, entry, steps, total } = data;
  const [hover, setHover] = useState<HoverState | null>(null);

  if (total === 0 || width <= 0 || height <= 0) return null;

  /* ─── Yerleşim sabitleri ─── */
  const padT = 50;
  const padB = 40;
  const padL = sources.length > 0 ? 110 : 80;
  const padR = 150;
  const nodeW = 14;
  const innerW = Math.max(width - padL - padR, 100);
  const innerH = Math.max(height - padT - padB, 100);

  // Omurga (yeşil nehir) iç alanın üst ~%55'ini kaplar;
  // çıkışlar sağ altta ~%40'lık bantta yığılır.
  const spineMaxH = innerH * 0.55;
  const spineCenterY = padT + spineMaxH / 2 + 10;

  const yScale = (v: number): number => Math.max((v / Math.max(total, 1)) * spineMaxH, 4);

  /* ─── Sütunlar ─── */
  const hasSources = sources.length > 0;
  const stepCount = steps.length;
  const colCount = (hasSources ? 1 : 0) + 1 + stepCount;
  const colX = (i: number): number => padL + (innerW * i) / Math.max(colCount - 1, 1);

  const sourceCol = 0;
  const entryCol = hasSources ? 1 : 0;
  const stepColIdx = (i: number): number => entryCol + 1 + i;

  /* ─── Kaynak dikdörtgenleri (solda, dikey yığılı ve ortalı) ─── */
  const sourceGap = 12;
  const totalSrcH =
    sources.reduce((s, x) => s + yScale(x.count), 0) + Math.max(0, sources.length - 1) * sourceGap;
  const srcStartY = padT + (innerH - totalSrcH) / 2;
  const sourceRects: { x: number; y: number; w: number; h: number; name: string; count: number }[] =
    [];
  sources.reduce((y, s) => {
    const h = yScale(s.count);
    sourceRects.push({ x: colX(sourceCol), y, w: nodeW, h, name: s.name, count: s.count });
    return y + h + sourceGap;
  }, srcStartY);

  /* ─── Giriş dikdörtgeni ─── */
  const entryH = yScale(entry.count);
  const entryRect = {
    x: colX(entryCol),
    y: spineCenterY - entryH / 2,
    w: nodeW,
    h: entryH,
    label: entry.label,
    count: entry.count,
  };

  /* ─── Adım düğümleri (omurga, dikey ortalı, her adımda daralan) ─── */
  interface PassRect {
    x: number;
    y: number;
    w: number;
    h: number;
    label: string;
    visitors: number;
    convRate: number;
    exitCount: number;
    prevConvRate?: number | null;
  }
  const passRects: PassRect[] = steps.map((st, i) => {
    const h = yScale(st.visitors);
    return {
      x: colX(stepColIdx(i)),
      y: spineCenterY - h / 2,
      w: nodeW,
      h,
      label: st.label,
      visitors: st.visitors,
      convRate: st.convRate,
      exitCount: st.exitCount,
      prevConvRate: st.prevConvRate ?? null,
    };
  });

  /* ─── Çıkış düğümleri (sağ kenar, omurganın altında yığılı) ───
   * Ters sıra: SONRAKİ adımların çıkışları üstte durur; kurdeleleri daha kısa dikey
   * mesafe kat eder ve önceki adımların kurdelelerinin üzerinden geçmez. */
  const exitData = steps
    .map((st, i) => ({ stepIndex: i, count: st.exitCount, label: st.label }))
    .filter((e) => e.count > 0)
    .reverse();

  const exitGap = 14;
  const exitX = padL + innerW - nodeW;
  const exitTopY = spineCenterY + spineMaxH / 2 + 36;
  const totalExitH =
    exitData.reduce((s, e) => s + yScale(e.count), 0) + Math.max(0, exitData.length - 1) * exitGap;
  const availExitH = padT + innerH - exitTopY;
  const exitScale = totalExitH > availExitH ? availExitH / totalExitH : 1;
  const exitRects: {
    x: number;
    y: number;
    w: number;
    h: number;
    count: number;
    label: string;
    stepIndex: number;
  }[] = [];
  exitData.reduce((y, e) => {
    const h = Math.max(yScale(e.count) * exitScale, 4);
    exitRects.push({ x: exitX, y, w: nodeW, h, count: e.count, label: e.label, stepIndex: e.stepIndex });
    return y + h + exitGap * exitScale;
  }, exitTopY);

  /* ─── Kurdele yolu (yumuşak Bezier) ─── */
  const ribbon = (
    sx: number,
    sy0: number,
    sy1: number,
    tx: number,
    ty0: number,
    ty1: number,
  ): string => {
    const mx = (sx + tx) / 2;
    return [
      `M ${sx},${sy0}`,
      `C ${mx},${sy0} ${mx},${ty0} ${tx},${ty0}`,
      `L ${tx},${ty1}`,
      `C ${mx},${ty1} ${mx},${sy1} ${sx},${sy1}`,
      'Z',
    ].join(' ');
  };

  /* ─── Akış bağlantıları ─── */
  interface Flow {
    d: string;
    fill: string;
    title: string;
    sub: string;
  }
  const flows: Flow[] = [];

  // Kaynak → Giriş kurdeleleri
  if (hasSources) {
    sourceRects.reduce((entryCursor, s) => {
      const sliceH = (s.count / Math.max(entry.count, 1)) * entryRect.h;
      flows.push({
        d: ribbon(s.x + s.w, s.y, s.y + s.h, entryRect.x, entryCursor, entryCursor + sliceH),
        fill: '#d1d5db',
        title: s.name,
        sub: `${tr(s.count)} oturum`,
      });
      return entryCursor + sliceH;
    }, entryRect.y);
  }

  // Omurga akışları (Giriş → adım 1, adım N → adım N+1) ve çıkış çatalları.
  passRects.reduce<{ x: number; y: number; w: number; h: number; count: number }>(
    (prev, p, i) => {
      const incoming = p.visitors + p.exitCount;
      const useFrac = incoming / Math.max(prev.count, 1);
      const useH = prev.h * useFrac;
      const srcTop = prev.y;
      const passSrcH = (p.visitors / Math.max(incoming, 1)) * useH;
      const passSrcBot = srcTop + passSrcH;

      flows.push({
        d: ribbon(prev.x + prev.w, srcTop, passSrcBot, p.x, p.y, p.y + p.h),
        fill: '#86efac',
        title: p.label,
        sub:
          p.prevConvRate !== null && p.prevConvRate !== undefined
            ? `${tr(p.visitors)} (%${oran(p.convRate)}) · önceki %${oran(p.prevConvRate)}`
            : `${tr(p.visitors)} (%${oran(p.convRate)})`,
      });

      if (p.exitCount > 0) {
        const eRect = exitRects.find((e) => e.stepIndex === i);
        if (eRect) {
          flows.push({
            d: ribbon(
              prev.x + prev.w,
              passSrcBot,
              srcTop + useH,
              eRect.x,
              eRect.y,
              eRect.y + eRect.h,
            ),
            fill: '#fca5a5',
            title: `${p.label} adımında çıkış`,
            sub: `${tr(p.exitCount)} terk`,
          });
        }
      }

      return { x: p.x, y: p.y, w: p.w, h: p.h, count: p.visitors };
    },
    { x: entryRect.x, y: entryRect.y, w: entryRect.w, h: entryRect.h, count: entry.count },
  );

  /* ─── Çizim ─── */
  const showTip = (e: React.MouseEvent<SVGElement>, title: string, sub: string): void => {
    const rect = (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect();
    setHover({ x: e.clientX - rect.left + 12, y: e.clientY - rect.top + 12, title, sub });
  };
  const hideTip = (): void => setHover(null);

  return (
    <div style={{ width, height, position: 'relative' }} onMouseLeave={hideTip}>
      <svg width={width} height={height}>
        {/* Akış kurdeleleri (düğümler üstte kalsın diye önce çizilir) */}
        {flows.map((f, i) => (
          <path
            key={i}
            d={f.d}
            fill={f.fill}
            fillOpacity={0.55}
            stroke="none"
            onMouseMove={(e) => showTip(e, f.title, f.sub)}
            onMouseLeave={hideTip}
          />
        ))}

        {/* Kaynak düğümleri */}
        {sourceRects.map((s, i) => (
          <g
            key={`src-${i}`}
            onMouseMove={(e) => showTip(e, s.name, `${tr(s.count)} oturum`)}
            onMouseLeave={hideTip}
          >
            <rect x={s.x} y={s.y} width={s.w} height={s.h} rx={3} fill="#9ca3af" />
            <text
              x={s.x - 10}
              y={s.y + s.h / 2 - 6}
              textAnchor="end"
              fill="#374151"
              fontSize={12}
              fontWeight={600}
            >
              {s.name}
            </text>
            <text x={s.x - 10} y={s.y + s.h / 2 + 8} textAnchor="end" fill="#6b7280" fontSize={11}>
              {tr(s.count)}
            </text>
          </g>
        ))}

        {/* Giriş düğümü */}
        <g
          onMouseMove={(e) => showTip(e, entryRect.label, `${tr(entryRect.count)} oturum`)}
          onMouseLeave={hideTip}
        >
          <rect
            x={entryRect.x}
            y={entryRect.y}
            width={entryRect.w}
            height={entryRect.h}
            rx={3}
            fill="#3b82f6"
          />
          <text
            x={entryRect.x + entryRect.w + 10}
            y={entryRect.y - 8}
            fill="#374151"
            fontSize={12}
            fontWeight={600}
          >
            {entryRect.label.length > 22 ? `${entryRect.label.slice(0, 20)}…` : entryRect.label}
          </text>
          <text
            x={entryRect.x + entryRect.w + 10}
            y={entryRect.y + 8}
            fill="#6b7280"
            fontSize={11}
          >
            {tr(entryRect.count)}
          </text>
        </g>

        {/* Adım düğümleri */}
        {passRects.map((p, i) => {
          const short = p.label.length > 18 ? `${p.label.slice(0, 16)}…` : p.label;
          return (
            <g
              key={`pass-${i}`}
              onMouseMove={(e) => showTip(e, p.label, `${tr(p.visitors)} • %${oran(p.convRate)}`)}
              onMouseLeave={hideTip}
            >
              <rect x={p.x} y={p.y} width={p.w} height={p.h} rx={3} fill="#22c55e" />
              <text x={p.x + p.w + 8} y={p.y - 8} fill="#374151" fontSize={12} fontWeight={600}>
                {short}
              </text>
              <text x={p.x + p.w + 8} y={p.y + 8} fill="#6b7280" fontSize={11}>
                {tr(p.visitors)} • %{oran(p.convRate)}
              </text>
            </g>
          );
        })}

        {/* Çıkış düğümleri */}
        {exitRects.map((e, i) => (
          <g
            key={`exit-${i}`}
            onMouseMove={(ev) => showTip(ev, `${e.label} adımında çıkış`, `${tr(e.count)} terk`)}
            onMouseLeave={hideTip}
          >
            <rect x={e.x} y={e.y} width={e.w} height={e.h} rx={3} fill="#ef4444" />
            <text
              x={e.x + e.w + 8}
              y={e.y + e.h / 2 - 4}
              fill="#374151"
              fontSize={12}
              fontWeight={600}
            >
              Çıkış
            </text>
            <text x={e.x + e.w + 8} y={e.y + e.h / 2 + 10} fill="#6b7280" fontSize={11}>
              {tr(e.count)}
            </text>
          </g>
        ))}
      </svg>

      {hover ? (
        <div
          style={{ position: 'absolute', left: hover.x, top: hover.y, pointerEvents: 'none' }}
          className="z-10 min-w-[160px] rounded-xl border border-[#e5e7eb] bg-white p-3 shadow-lg"
        >
          <p className="text-[13px] font-semibold text-[#111827]">{hover.title}</p>
          <p className="mt-0.5 text-[12px] text-[#6b7280]">{hover.sub}</p>
        </div>
      ) : null}
    </div>
  );
}

/** ResizeObserver sarmalayıcı — grafik kabın boyutuna uyar. */
export default function FunnelChartContainer({ data }: { data: FunnelModel }) {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });

  useEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const ro = new ResizeObserver(() => {
      setSize({ w: el.clientWidth, h: el.clientHeight });
    });
    ro.observe(el);
    setSize({ w: el.clientWidth, h: el.clientHeight });
    return () => ro.disconnect();
  }, []);

  return (
    <div ref={ref} className="h-full w-full">
      {size.w > 0 && size.h > 0 ? (
        <FunnelChart data={data} width={size.w} height={size.h} />
      ) : null}
    </div>
  );
}

export { FunnelChart };
